import React, { ReactNode, useMemo } from 'react';
import { captureException } from '@sentry/nextjs';
import {
  Chat,
  Flow,
  Flows,
  LineOfBusiness,
  SupportedLanguageCode,
  useConversationSDK,
} from '@spotify-internal/pillow-talk';

import { PillowTalkConfig, Vertical } from 'utils/vertical';
import { sendCounterMetric, Whats } from 'utils/metrics';
import { CHAT_BOT_CLIENT_ID } from 'utils/config';
import { DEFAULT_LOCALE } from 'utils/i18n/localeConfig';
import { isSupportedMessagingLocale } from 'utils/market/market';

import { MessagingAvailabilityReason } from './types';
import { MessagingContext } from './MessagingContext';

interface PillowTalkMessagingProviderProps {
  vertical: Vertical;
  lineOfBusiness: LineOfBusiness;
  locale: string;
  redirectUri: string;
  children: ReactNode;
  showLoginDialog: boolean;
  allowFirstMessage: boolean;
  flow?: Flow;
}

const PillowTalkMessagingProvider = ({
  vertical,
  lineOfBusiness,
  locale,
  children,
  redirectUri,
  showLoginDialog,
  allowFirstMessage,
  flow,
}: PillowTalkMessagingProviderProps) => {
  const language = locale as SupportedLanguageCode;
  const { startConversation, conversation, sdkStatus } = useConversationSDK({
    clientId: CHAT_BOT_CLIENT_ID,
    language,
    redirectUri,
    lineOfBusiness,
  });

  const status = useMemo(() => {
    const isSlingshot = process.env.NEXT_PUBLIC_IS_SLINGSHOT === 'true';

    if (isSlingshot) {
      return { isAvailable: true, reason: MessagingAvailabilityReason.AVAILABLE };
    }

    if (sdkStatus.loading) {
      return { isAvailable: false, reason: MessagingAvailabilityReason.LOADING };
    }

    if (sdkStatus.availabilityStatus !== 'available') {
      const resolution = MessagingAvailabilityReason.PILLOW_TALK_ERROR;
      sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_SETUP, { resolution, vertical });
      return { isAvailable: false, reason: resolution };
    }

    const resolution = MessagingAvailabilityReason.AVAILABLE;
    sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_SETUP, { resolution, vertical });
    return { isAvailable: true, reason: resolution };
  }, [sdkStatus.loading, sdkStatus.availabilityStatus, vertical]);

  const value = {
    status,
    conversation,
    startMessaging: status.isAvailable
      ? async () => {
          try {
            await startConversation({ showLoginDialog, allowFirstMessage, flow });
            sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_START, { vertical });
          } catch (err) {
            captureException(err);
          }
        }
      : undefined,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
      {status.isAvailable && <Chat conversation={conversation} />}
    </MessagingContext.Provider>
  );
};

interface Props {
  children: React.ReactNode;
  locale: string;
  configurePillowTalk: PillowTalkConfig;
  vertical: Vertical;
  messagingLocales: Readonly<Array<string>>;
}

export const PillowTalkWrapper = ({ children, locale, configurePillowTalk, vertical, messagingLocales }: Props) => {
  const { lineOfBusiness, redirectUri, showLoginDialog } = configurePillowTalk;

  const language = isSupportedMessagingLocale(locale, messagingLocales) ? locale : DEFAULT_LOCALE;
  const allowFirstMessage = vertical === Vertical.CONSUMER;
  const isPremiumCloseAccountUrl = () => global.window?.location.pathname.includes('premium-close-account');

  return (
    <PillowTalkMessagingProvider
      vertical={vertical}
      lineOfBusiness={lineOfBusiness}
      locale={language}
      redirectUri={redirectUri}
      showLoginDialog={showLoginDialog}
      allowFirstMessage={allowFirstMessage}
      flow={isPremiumCloseAccountUrl() ? Flows.CLOSE_ACCOUNT : undefined}
    >
      {children}
    </PillowTalkMessagingProvider>
  );
};
